import React, { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import cx from 'clsx';

import { useCheckoutSessionContext } from 'hooks/context/checkoutSession';
import { useCloverPaymentIframeContext } from 'hooks/context/cloverPaymentIframe';
import { useMerchantPaymentConfigContext } from 'hooks/context/merchantPaymentConfig';
import { Icon } from 'components/Icon';
import { isICASupported } from 'utils/Ach';

import { Note } from './Note';
import { AchPaymentInfo } from '../AchPaymentInfo';
import { PaymentInfo, PaymentInfoErrors } from '../PaymentInfo';
import { GiftCardSection } from '../GiftCardSection';
import { PAYMENT_METHODS_OPTIONS } from '../PaymentMethods';

import { validateNote } from 'utils/Regex';

import styles from './styles.module.scss';

type PaymentFormProps = {
  paymentMethod: string;
  setShowPaymentErrors: Dispatch<SetStateAction<boolean>>;
  setPaymentInfoErrors: Dispatch<SetStateAction<PaymentInfoErrors | undefined>>;
  setEnableSubmitButton: Dispatch<SetStateAction<boolean>>;
  orderNote: string;
  setOrderNote: Dispatch<SetStateAction<string>>;
  setShowNoteError: Dispatch<SetStateAction<boolean>>;
  displayFormErrors: boolean;
  showPaymentApiError: boolean;
  paymentApiErrorMessage?: string;
  hasMaxPayAttemptsError: boolean;
  errorMsg: string;
};

const PaymentSourceForm: FC<PaymentFormProps> = ({
  setShowPaymentErrors,
  setPaymentInfoErrors,
  setEnableSubmitButton,
  setShowNoteError,
  setOrderNote,
  orderNote,
  displayFormErrors,
  showPaymentApiError,
  hasMaxPayAttemptsError,
  errorMsg,
  paymentMethod,
  paymentApiErrorMessage,
}) => {
  const { t } = useTranslation();
  const { merchantSettings } = useCheckoutSessionContext();
  const {
    cloverElementsInstance,
    cloverInstance,
  } = useCloverPaymentIframeContext();
  const { merchantPaymentConfig } = useMerchantPaymentConfigContext();
  const { giftcards } = queryString.parse(window.location.search);
  // const isGiftCardsSupported = merchantPaymentConfig?.gift_card?.supported && giftcards === 'true';
  const isGiftCardsSupported = giftcards === 'true';
  const isNoteFeatureEnabled =
    merchantSettings?.HCO_NOTE_SETTING_ENABLED === 'true' ||
    merchantSettings?.HCO_NOTE_SETTING_ENABLED === '1';
  const isOrderNoteEnabled =
    isNoteFeatureEnabled &&
    (merchantSettings?.ORDER_NOTE_ENABLED === 'true' ||
      merchantSettings?.ORDER_NOTE_ENABLED === '1');

  const isAchIcaSupported = isICASupported(merchantPaymentConfig);

  const isCCSelected = paymentMethod === PAYMENT_METHODS_OPTIONS[0].key;
  const isAchSelected =
    PAYMENT_METHODS_OPTIONS.length > 1 &&
    paymentMethod === PAYMENT_METHODS_OPTIONS[1].key;

  if (!cloverElementsInstance || !cloverInstance) return null;

  return (
    <>
      {isCCSelected && (
        <section>
          <h3 className={styles.sectionInfo}>{t('cardInformation')}</h3>
          <PaymentInfo
            cloverElementsInstance={cloverElementsInstance}
            toggleShowErrors={setShowPaymentErrors}
            onChange={setPaymentInfoErrors}
            toggleButtonDisableState={setEnableSubmitButton}
          />
        </section>
      )}

      {isAchIcaSupported && isAchSelected && (
        <section>
          <AchPaymentInfo
            cloverElementsInstance={cloverElementsInstance}
            toggleButtonDisableState={setEnableSubmitButton}
          />
        </section>
      )}

      {isGiftCardsSupported && (
        <GiftCardSection cloverElementsInstance={cloverElementsInstance} cloverInstance={cloverInstance}/>
      )}

      <>
        {isOrderNoteEnabled && (
          <Note
            note={orderNote}
            onChange={(e) => {
              setOrderNote(e.target.value);
              setShowNoteError(!validateNote(e.target.value));
            }}
          />
        )}
        <div
          aria-live="polite"
          role="alert"
          className={cx(styles.paymentError, {
            [styles.paymentErrorHide]:
              !displayFormErrors &&
              !showPaymentApiError &&
              !hasMaxPayAttemptsError,
          })}
        >
          {displayFormErrors && errorMsg && (
            <p>
              <Icon className={styles.icon} name="error" />
              {errorMsg}
            </p>
          )}

          {showPaymentApiError && (
            <p>
              <Icon className={styles.icon} name="error" />
              {paymentApiErrorMessage ? paymentApiErrorMessage : t('paymentError')}
            </p>
          )}
          {hasMaxPayAttemptsError && (
            <p>
              <Icon className={styles.icon} name="error" />
              {t('maxPayAttemptsError')}
            </p>
          )}
        </div>
      </>
    </>
  );
};

export { PaymentSourceForm };
